import { env } from '@/config/env';
import {
  User,
  UserStatus,
  Role,
  UserFilterState,
  UserMetricsSummary,
  UserCreationInput,
  UserUpdateInput,
  UserActivityEntry,
} from '@/types/domain/user';
import { MOCK_USERS, MOCK_DEPARTMENTS, MOCK_PLANTS } from '@/mocks/database';
import { ROLE_PERMISSIONS_MAP } from '@/config/permissions';
import { auditService } from '@/services/audit/AuditService';
import { notifyGlobal } from '@/context/NotificationContext';
import { getEntityById } from './pagination';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class MockUserManagementService {
  private users: User[] = [...MOCK_USERS];
  private activityLogs: Map<string, UserActivityEntry[]> = new Map();

  constructor() {
    this.seedInitialActivity();
  }

  private seedInitialActivity() {
    this.users.forEach((user) => {
      const logs: UserActivityEntry[] = [
        {
          id: `act-${user.id}-1`,
          userId: user.id,
          action: 'Account Provisioned',
          description: `User account created by Enterprise Administrator. Designated role: ${user.role}.`,
          actor: 'Alexander Hamilton',
          actorRole: 'System Administrator',
          timestamp: user.createdAt || '2026-06-01T08:00:00Z',
        },
      ];

      if (user.status === 'INVITATION_PENDING') {
        logs.unshift({
          id: `act-${user.id}-2`,
          userId: user.id,
          action: 'Invitation Dispatched',
          description: `Activation email sent to ${user.email}. Pending employee verification.`,
          actor: 'Automated Identity Gateway',
          actorRole: 'System Identity',
          timestamp: user.invitationSentAt || '2026-08-30T10:00:00Z',
        });
      } else {
        logs.unshift({
          id: `act-${user.id}-2`,
          userId: user.id,
          action: 'Invitation Accepted',
          description: 'Employee completed initial identity onboarding and verified credentials.',
          actor: user.name,
          actorRole: user.role,
          timestamp: '2026-06-02T10:15:00Z',
        });
      }

      if (user.lastLoginAt) {
        logs.unshift({
          id: `act-${user.id}-3`,
          userId: user.id,
          action: 'ERP Session Authenticated',
          description: `Successful login from enterprise network (Client: Web Portal).`,
          actor: user.name,
          actorRole: user.role,
          timestamp: user.lastLoginAt,
        });
      }

      if (user.status === 'BLOCKED') {
        logs.unshift({
          id: `act-${user.id}-4`,
          userId: user.id,
          action: 'Account Quarantined',
          description: user.statusReason || 'Security hold placed by Enterprise Administrator.',
          actor: 'Alexander Hamilton',
          actorRole: 'System Administrator',
          timestamp: '2026-08-20T09:45:00Z',
        });
      }

      this.activityLogs.set(user.id, logs);
    });
  }

  async getUserMetrics(): Promise<UserMetricsSummary> {
    await delay(env.mockDelayMs / 2);
    const totalUsers = this.users.length;
    const activeUsers = this.users.filter((u) => u.status === 'ACTIVE').length;
    const pendingInvitations = this.users.filter((u) => u.status === 'INVITATION_PENDING').length;
    const blockedUsers = this.users.filter((u) => u.status === 'BLOCKED').length;
    const inactiveUsers = this.users.filter((u) => u.status === 'INACTIVE').length;
    const lockedUsers = this.users.filter((u) => u.status === 'LOCKED').length;

    return {
      totalUsers,
      activeUsers,
      pendingInvitations,
      blockedUsers,
      inactiveUsers,
      lockedUsers,
    };
  }

  async getUsers(filters?: Partial<UserFilterState>): Promise<{ items: User[]; total: number }> {
    await delay(env.mockDelayMs);
    let result = [...this.users];

    // Search filter
    if (filters?.search && filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      result = result.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.employeeId.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.departmentName.toLowerCase().includes(q) ||
          u.role.toLowerCase().includes(q) ||
          (u.designation && u.designation.toLowerCase().includes(q))
      );
    }

    // Status filter
    if (filters?.status && filters.status !== 'ALL') {
      result = result.filter((u) => u.status === filters.status);
    }

    // Role filter
    if (filters?.role && filters.role !== 'ALL') {
      result = result.filter((u) => u.role === filters.role);
    }

    // Department filter
    if (filters?.department && filters.department !== 'ALL') {
      result = result.filter((u) => u.departmentCode === filters.department);
    }

    // Plant filter
    if (filters?.plant && filters.plant !== 'ALL') {
      result = result.filter((u) => u.plantCode === filters.plant);
    }

    // Invitation status filter
    if (filters?.invitationStatus && filters.invitationStatus !== 'ALL') {
      result = result.filter((u) => u.invitationStatus === filters.invitationStatus);
    }

    // Sort
    const sortBy = filters?.sortBy || 'createdAt';
    const sortOrder = filters?.sortOrder || 'desc';

    result.sort((a, b) => {
      const valA = a[sortBy] ?? '';
      const valB = b[sortBy] ?? '';
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    const total = result.length;
    const page = filters?.page || 1;
    const pageSize = filters?.pageSize || 25;
    const startIndex = (page - 1) * pageSize;
    const items = result.slice(startIndex, startIndex + pageSize);

    return { items, total };
  }

  async getUserById(id: string): Promise<User | null> {
    await delay(env.mockDelayMs);
    return getEntityById(this.users, id, 'id') || getEntityById(this.users, id, 'employeeId') || null;
  }

  async createUser(
    input: UserCreationInput,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<User> {
    await delay(env.mockDelayMs);

    // Validate email uniqueness
    const normalizedEmail = input.email.trim().toLowerCase();
    const emailExists = this.users.some((u) => u.email.toLowerCase() === normalizedEmail);
    if (emailExists) {
      throw new Error(`User with email "${input.email}" already exists in the enterprise directory.`);
    }

    // Validate employeeId uniqueness
    const normalizedEmpId = input.employeeId.trim().toUpperCase();
    const empIdExists = this.users.some((u) => u.employeeId.toUpperCase() === normalizedEmpId);
    if (empIdExists) {
      throw new Error(`Employee ID "${input.employeeId}" is already assigned to another user.`);
    }

    // Prevent arbitrary assignment of SAP_INTEGRATION
    if (input.role === 'SAP_INTEGRATION') {
      throw new Error('SAP Integration Identity is system-managed and cannot be provisioned manually.');
    }

    const dept = MOCK_DEPARTMENTS.find((d) => d.code === input.departmentCode) || MOCK_DEPARTMENTS[0]!;
    const plant = MOCK_PLANTS.find((p) => p.code === input.plantCode) || MOCK_PLANTS[0]!;
    const id = `usr-${Date.now()}`;
    const now = new Date().toISOString();

    const authorityLimit =
      input.approvalAuthorityLimit !== undefined
        ? input.approvalAuthorityLimit
        : input.role === 'ADMIN'
        ? 99999999
        : input.role === 'MANAGEMENT'
        ? 10000000
        : input.role === 'NFA_LFA_APPROVER'
        ? 5000000
        : input.role === 'PROCUREMENT_MANAGER'
        ? 1000000
        : input.role === 'FINANCE'
        ? 500000
        : input.role === 'PROCUREMENT'
        ? 250000
        : input.role === 'MANAGER'
        ? 50000
        : 10000;

    let managerName = 'Marcus Vance';
    if (input.managerId) {
      const mgr = this.users.find((u) => u.id === input.managerId);
      if (mgr) managerName = mgr.name;
    }

    const newUser: User = {
      id,
      employeeId: normalizedEmpId,
      name: input.name.trim(),
      email: normalizedEmail,
      phone: input.phone || '+91 98000 00000',
      designation: input.designation,
      role: input.role,
      roles: [input.role],
      departmentCode: dept.code,
      departmentName: dept.name,
      plantCode: plant.code,
      businessUnit: 'BU-GLOBAL',
      companyCode: input.companyCode || plant.companyCode,
      costCenterCode: input.costCenterCode || dept.costCenterCode || 'CC-1010',
      managerId: input.managerId || 'usr-2',
      managerName,
      approvalAuthorityLimit: authorityLimit,
      status: 'INVITATION_PENDING',
      invitationStatus: 'INVITATION_PENDING',
      invitationSentAt: now,
      createdAt: now,
      updatedAt: now,
      mfaEnabled: false,
      ssoEnabled: true,
      accessScope: {
        departments: input.scopedDepartments || [dept.code],
        plants: input.scopedPlants || [plant.code],
        businessUnits: ['BU-GLOBAL'],
        companyCodes: [input.companyCode || plant.companyCode],
        ownership: input.dataScopeOwnership || 'DEPARTMENT',
      },
      permissions: ROLE_PERMISSIONS_MAP[input.role] || ROLE_PERMISSIONS_MAP.EMPLOYEE,
    };

    this.users.unshift(newUser);

    // Initial activity log
    const activityEntry: UserActivityEntry = {
      id: `act-${newUser.id}-${Date.now()}`,
      userId: newUser.id,
      action: 'Account Provisioned',
      description: `Account created with role ${newUser.role}. Invitation dispatched to ${newUser.email}.`,
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: now,
    };
    this.activityLogs.set(newUser.id, [activityEntry]);

    // Audit log
    auditService.log(
      'USER_CREATED',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: newUser.id, employeeId: newUser.employeeId, email: newUser.email, role: newUser.role }
    );
    auditService.log(
      'INVITATION_SENT',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: newUser.id, recipientEmail: newUser.email }
    );

    // Toast notification
    notifyGlobal(
      'User Provisioned Successfully',
      `Invitation dispatched to ${newUser.email} with role ${newUser.role}.`,
      'StatusChange',
      newUser.id
    );

    return newUser;
  }

  async updateUser(
    id: string,
    input: UserUpdateInput,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<User> {
    await delay(env.mockDelayMs);
    const userIndex = this.users.findIndex((u) => u.id === id || u.employeeId === id);
    if (userIndex === -1) throw new Error(`User with ID ${id} not found.`);

    const current = this.users[userIndex]!;

    // Self-protection: Admin cannot remove admin role from themselves
    if (currentAdmin && currentAdmin.id === current.id && input.role && input.role !== 'ADMIN') {
      throw new Error('Security Restriction: You cannot remove System Administrator access from your own account.');
    }

    // Last-admin protection: If user is ADMIN and role is changing, ensure another active admin exists
    if (current.role === 'ADMIN' && input.role && input.role !== 'ADMIN') {
      const activeAdminCount = this.users.filter((u) => u.role === 'ADMIN' && u.status === 'ACTIVE').length;
      if (activeAdminCount <= 1) {
        throw new Error('Security Restriction: You cannot demote the last active System Administrator.');
      }
    }

    const isRoleChanged = input.role && input.role !== current.role;
    const oldRole = current.role;

    let updatedDept = current.departmentName;
    if (input.departmentCode && input.departmentCode !== current.departmentCode) {
      const d = MOCK_DEPARTMENTS.find((dept) => dept.code === input.departmentCode);
      if (d) updatedDept = d.name;
    }

    let updatedManagerName = current.managerName;
    if (input.managerId && input.managerId !== current.managerId) {
      const mgr = this.users.find((u) => u.id === input.managerId);
      if (mgr) updatedManagerName = mgr.name;
    }

    const updatedUser: User = {
      ...current,
      name: input.name ? input.name.trim() : current.name,
      phone: input.phone !== undefined ? input.phone : current.phone,
      designation: input.designation !== undefined ? input.designation : current.designation,
      departmentCode: input.departmentCode || current.departmentCode,
      departmentName: updatedDept,
      plantCode: input.plantCode || current.plantCode,
      companyCode: input.companyCode || current.companyCode,
      costCenterCode: input.costCenterCode || current.costCenterCode,
      managerId: input.managerId || current.managerId,
      managerName: updatedManagerName,
      role: input.role || current.role,
      roles: input.role ? [input.role] : current.roles,
      approvalAuthorityLimit:
        input.approvalAuthorityLimit !== undefined ? input.approvalAuthorityLimit : current.approvalAuthorityLimit,
      updatedAt: new Date().toISOString(),
      permissions: input.role ? ROLE_PERMISSIONS_MAP[input.role] : current.permissions,
      accessScope: {
        ...current.accessScope,
        ownership: input.dataScopeOwnership || current.accessScope.ownership,
        departments: input.scopedDepartments || current.accessScope.departments,
        plants: input.scopedPlants || current.accessScope.plants,
      },
    };

    this.users[userIndex] = updatedUser;

    // Log user activity
    const now = new Date().toISOString();
    const existingLogs = this.activityLogs.get(current.id) || [];
    const changeDescription = isRoleChanged
      ? `Role modified from ${oldRole} to ${updatedUser.role}. Permissions updated.`
      : `Profile & organizational attributes updated.`;

    existingLogs.unshift({
      id: `act-${current.id}-${Date.now()}`,
      userId: current.id,
      action: isRoleChanged ? 'Role & Authority Changed' : 'Profile Updated',
      description: changeDescription,
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: now,
    });
    this.activityLogs.set(current.id, existingLogs);

    // Audit log
    auditService.log(
      isRoleChanged ? 'ROLE_CHANGED' : 'USER_UPDATED',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: current.id, before: { role: oldRole }, after: { role: updatedUser.role } }
    );

    notifyGlobal(
      isRoleChanged ? `Role Changed: ${updatedUser.role}` : 'User Profile Updated',
      `${updatedUser.name} (${updatedUser.employeeId}) profile updated successfully.`,
      'StatusChange',
      updatedUser.id
    );

    return updatedUser;
  }

  async activateUser(
    id: string,
    reason?: string,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<User> {
    await delay(env.mockDelayMs);
    const user = await this.getUserById(id);
    if (!user) throw new Error(`User with ID ${id} not found.`);

    user.status = 'ACTIVE';
    user.invitationStatus = 'ACCEPTED';
    user.statusReason = reason || 'Activated by Enterprise Administrator';
    user.updatedAt = new Date().toISOString();

    // Log activity
    const existingLogs = this.activityLogs.get(user.id) || [];
    existingLogs.unshift({
      id: `act-${user.id}-${Date.now()}`,
      userId: user.id,
      action: 'Account Activated',
      description: `User account transitioned to ACTIVE. ${reason ? `Reason: ${reason}` : ''}`,
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: new Date().toISOString(),
    });
    this.activityLogs.set(user.id, existingLogs);

    auditService.log(
      'USER_ACTIVATED',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: user.id, reason }
    );

    notifyGlobal(
      'User Account Activated',
      `${user.name} (${user.employeeId}) is now ACTIVE with full authorized access.`,
      'StatusChange',
      user.id
    );

    return user;
  }

  async deactivateUser(
    id: string,
    reason: string,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<User> {
    await delay(env.mockDelayMs);
    const user = await this.getUserById(id);
    if (!user) throw new Error(`User with ID ${id} not found.`);

    // Self-protection
    if (currentAdmin && currentAdmin.id === user.id) {
      throw new Error('Security Restriction: You cannot deactivate your own administrative account.');
    }

    // Last-admin protection
    if (user.role === 'ADMIN') {
      const activeAdminCount = this.users.filter((u) => u.role === 'ADMIN' && u.status === 'ACTIVE').length;
      if (activeAdminCount <= 1) {
        throw new Error('Security Restriction: You cannot deactivate the last active System Administrator.');
      }
    }

    user.status = 'INACTIVE';
    user.statusReason = reason;
    user.updatedAt = new Date().toISOString();

    const existingLogs = this.activityLogs.get(user.id) || [];
    existingLogs.unshift({
      id: `act-${user.id}-${Date.now()}`,
      userId: user.id,
      action: 'Account Deactivated',
      description: `User account deactivated. Reason: ${reason}`,
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: new Date().toISOString(),
    });
    this.activityLogs.set(user.id, existingLogs);

    auditService.log(
      'USER_DEACTIVATED',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: user.id, reason }
    );

    notifyGlobal(
      'User Account Deactivated',
      `${user.name} (${user.employeeId}) deactivated. Access has been suspended.`,
      'StatusChange',
      user.id
    );

    return user;
  }

  async blockUser(
    id: string,
    reason: string,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<User> {
    await delay(env.mockDelayMs);
    const user = await this.getUserById(id);
    if (!user) throw new Error(`User with ID ${id} not found.`);

    // Self-protection
    if (currentAdmin && currentAdmin.id === user.id) {
      throw new Error('Security Restriction: You cannot quarantine or block your own administrative account.');
    }

    // Last-admin protection
    if (user.role === 'ADMIN') {
      const activeAdminCount = this.users.filter((u) => u.role === 'ADMIN' && u.status === 'ACTIVE').length;
      if (activeAdminCount <= 1) {
        throw new Error('Security Restriction: You cannot block the last active System Administrator.');
      }
    }

    user.status = 'BLOCKED';
    user.statusReason = reason;
    user.updatedAt = new Date().toISOString();

    const existingLogs = this.activityLogs.get(user.id) || [];
    existingLogs.unshift({
      id: `act-${user.id}-${Date.now()}`,
      userId: user.id,
      action: 'Account Quarantined / Blocked',
      description: `Security block applied. Reason: ${reason}`,
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: new Date().toISOString(),
    });
    this.activityLogs.set(user.id, existingLogs);

    auditService.log(
      'USER_BLOCKED',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: user.id, reason }
    );

    notifyGlobal(
      'User Account Blocked',
      `${user.name} (${user.employeeId}) placed under immediate security containment.`,
      'StatusChange',
      user.id
    );

    return user;
  }

  async unblockUser(
    id: string,
    reason?: string,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<User> {
    await delay(env.mockDelayMs);
    const user = await this.getUserById(id);
    if (!user) throw new Error(`User with ID ${id} not found.`);

    user.status = 'ACTIVE';
    user.statusReason = reason || 'Security block cleared by Enterprise Administrator';
    user.updatedAt = new Date().toISOString();

    const existingLogs = this.activityLogs.get(user.id) || [];
    existingLogs.unshift({
      id: `act-${user.id}-${Date.now()}`,
      userId: user.id,
      action: 'Security Block Cleared',
      description: `User account unblocked and restored to ACTIVE. ${reason ? `Reason: ${reason}` : ''}`,
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: new Date().toISOString(),
    });
    this.activityLogs.set(user.id, existingLogs);

    auditService.log(
      'USER_UNBLOCKED',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: user.id, reason }
    );

    notifyGlobal(
      'User Account Unblocked',
      `${user.name} (${user.employeeId}) access restored to ACTIVE.`,
      'StatusChange',
      user.id
    );

    return user;
  }

  async unlockUser(
    id: string,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<User> {
    await delay(env.mockDelayMs);
    const user = await this.getUserById(id);
    if (!user) throw new Error(`User with ID ${id} not found.`);

    user.status = 'ACTIVE';
    user.statusReason = 'Authentication lockout cleared by Administrator';
    user.updatedAt = new Date().toISOString();

    const existingLogs = this.activityLogs.get(user.id) || [];
    existingLogs.unshift({
      id: `act-${user.id}-${Date.now()}`,
      userId: user.id,
      action: 'Lockout Cleared',
      description: 'Failed attempt lockout cleared by Administrator. Normal login allowed.',
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: new Date().toISOString(),
    });
    this.activityLogs.set(user.id, existingLogs);

    auditService.log(
      'ACCOUNT_UNLOCKED',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: user.id }
    );

    notifyGlobal(
      'Account Lockout Cleared',
      `Login restrictions removed for ${user.name} (${user.employeeId}).`,
      'StatusChange',
      user.id
    );

    return user;
  }

  async resendInvitation(
    id: string,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<{ success: boolean; sentAt: string }> {
    await delay(env.mockDelayMs);
    const user = await this.getUserById(id);
    if (!user) throw new Error(`User with ID ${id} not found.`);

    const now = new Date().toISOString();
    user.invitationSentAt = now;
    user.updatedAt = now;

    const existingLogs = this.activityLogs.get(user.id) || [];
    existingLogs.unshift({
      id: `act-${user.id}-${Date.now()}`,
      userId: user.id,
      action: 'Invitation Re-Dispatched',
      description: `Secure invitation link re-sent to ${user.email}.`,
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: now,
    });
    this.activityLogs.set(user.id, existingLogs);

    auditService.log(
      'INVITATION_RESENT',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: user.id, recipientEmail: user.email }
    );

    notifyGlobal(
      'Invitation Resent',
      `A new activation email has been dispatched to ${user.email}.`,
      'StatusChange',
      user.id
    );

    return { success: true, sentAt: now };
  }

  async sendPasswordReset(
    id: string,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<{ success: boolean; timestamp: string }> {
    await delay(env.mockDelayMs);
    const user = await this.getUserById(id);
    if (!user) throw new Error(`User with ID ${id} not found.`);

    const now = new Date().toISOString();

    const existingLogs = this.activityLogs.get(user.id) || [];
    existingLogs.unshift({
      id: `act-${user.id}-${Date.now()}`,
      userId: user.id,
      action: 'Password Reset Triggered',
      description: `One-time secure password reset instruction dispatched to ${user.email}.`,
      actor: currentAdmin?.email || 'Alexander Hamilton',
      actorRole: currentAdmin?.role || 'System Administrator',
      timestamp: now,
    });
    this.activityLogs.set(user.id, existingLogs);

    auditService.log(
      'PASSWORD_RESET_TRIGGERED',
      { id: currentAdmin?.id, email: currentAdmin?.email, role: currentAdmin?.role },
      { userId: user.id, targetEmail: user.email }
    );

    notifyGlobal(
      'Password Reset Dispatched',
      `Secure password reset instructions sent to ${user.email}.`,
      'StatusChange',
      user.id
    );

    return { success: true, timestamp: now };
  }

  async getUserActivity(userId: string): Promise<UserActivityEntry[]> {
    await delay(env.mockDelayMs / 2);
    return this.activityLogs.get(userId) || [];
  }

  async bulkUpdateStatus(
    ids: string[],
    status: UserStatus,
    reason: string,
    currentAdmin?: { id: string; email: string; role: Role }
  ): Promise<{ updatedCount: number }> {
    await delay(env.mockDelayMs);
    let count = 0;

    for (const id of ids) {
      const u = this.users.find((user) => user.id === id);
      if (!u) continue;

      // Self-protection check
      if (currentAdmin && currentAdmin.id === u.id && (status === 'INACTIVE' || status === 'BLOCKED')) {
        continue;
      }

      // Last-admin check
      if (u.role === 'ADMIN' && (status === 'INACTIVE' || status === 'BLOCKED')) {
        const activeAdminCount = this.users.filter((usr) => usr.role === 'ADMIN' && usr.status === 'ACTIVE').length;
        if (activeAdminCount <= 1) continue;
      }

      u.status = status;
      u.statusReason = reason;
      u.updatedAt = new Date().toISOString();
      count++;
    }

    notifyGlobal(
      'Bulk Status Updated',
      `Updated ${count} user accounts to ${status}.`,
      'StatusChange'
    );

    return { updatedCount: count };
  }
}

export const mockUserManagementService = new MockUserManagementService();
